import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MoreVertical, Edit, Trash2, Plus } from 'lucide-react';
import SearchableSelect from './SearchableSelect';

const CreditCardManagement = ({ isViewer }) => {
  const [creditCards, setCreditCards] = useState([]);
  const [locations, setLocations] = useState([]);
  const [masterCards, setMasterCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCreditCard, setEditingCreditCard] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [formData, setFormData] = useState({
    entity: '',
    lastFourCC: '',
    lastFourBankAccount: '',
    notes: '',
    isActive: true,
    locationId: '',
    masterCardId: ''
  });

  // Dropdown states
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openDropdownCreditCard, setOpenDropdownCreditCard] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const fetchCreditCards = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (includeInactive) params.append('includeInactive', 'true');
      params.append('limit', '500');

      const response = await axios.get(`/api/credit-cards?${params}`);
      setCreditCards(response.data.creditCards);
      setError('');
    } catch (error) {
      console.error('Error fetching credit cards:', error);
      setError('Failed to fetch credit cards');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations/dropdown/active');
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchMasterCards = async () => {
    try {
      const response = await axios.get('/api/master-cards');
      setMasterCards(response.data);
    } catch (error) {
      console.error('Error fetching master cards:', error);
    }
  };

  useEffect(() => {
    fetchCreditCards();
    fetchLocations();
    fetchMasterCards();
  }, [searchTerm, includeInactive]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId &&
          !event.target.closest('.relative') &&
          !event.target.closest('.dropdown-menu')) {
        setOpenDropdownId(null);
        setOpenDropdownCreditCard(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.entity.trim()) {
      setError('Entity is required');
      return;
    }
    
    if (!formData.lastFourCC || formData.lastFourCC.length < 1 || formData.lastFourCC.length > 4 || !/^\d{1,4}$/.test(formData.lastFourCC)) {
      setError('Last 4 CC must be 1-4 digits');
      return;
    }

    if (!formData.lastFourBankAccount || formData.lastFourBankAccount.length < 1 || formData.lastFourBankAccount.length > 4 || !/^\d{1,4}$/.test(formData.lastFourBankAccount)) {
      setError('Last 4 Bank Account must be 1-4 digits');
      return;
    }

    try {
      if (editingCreditCard) {
        await axios.put(`/api/credit-cards/${editingCreditCard.id}`, formData);
        setSuccess('Credit card updated successfully');
      } else {
        await axios.post('/api/credit-cards', formData);
        setSuccess('Credit card created successfully');
      }
      
      setShowModal(false);
      setEditingCreditCard(null);
      setFormData({
        entity: '',
        lastFourCC: '',
        lastFourBankAccount: '',
        notes: '',
        isActive: true,
        locationId: '',
        masterCardId: ''
      });
      fetchCreditCards();
      setError('');
    } catch (error) {
      console.error('Error saving credit card:', error);
      setError(error.response?.data?.error || 'Failed to save credit card');
    }
  };

  const handleEdit = (creditCard) => {
    setEditingCreditCard(creditCard);
    setFormData({
      entity: creditCard.entity,
      lastFourCC: creditCard.lastFourCC,
      lastFourBankAccount: creditCard.lastFourBankAccount,
      notes: creditCard.notes || '',
      isActive: creditCard.isActive,
      locationId: creditCard.locationId || '',
      masterCardId: creditCard.masterCardId || ''
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (creditCard) => {
    if (!window.confirm(`Are you sure you want to delete "${creditCard.entity} (${creditCard.lastFourCC})"?`)) {
      return;
    }

    try {
      await axios.delete(`/api/credit-cards/${creditCard.id}`);
      setSuccess('Credit card deleted successfully');
      fetchCreditCards();
      setError('');
    } catch (error) {
      console.error('Error deleting credit card:', error);
      setError(error.response?.data?.error || 'Failed to delete credit card');
    }
  };

  const handleNewCreditCard = () => {
    setEditingCreditCard(null);
    setFormData({
      entity: '',
      lastFourCC: '',
      lastFourBankAccount: '',
      notes: '',
      isActive: true,
      locationId: '',
      masterCardId: ''
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCreditCard(null);
    setFormData({
      entity: '',
      lastFourCC: '',
      lastFourBankAccount: '',
      notes: '',
      isActive: true,
      locationId: '',
      masterCardId: ''
    });
    setError('');
  };

  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('csvFile', file);

    try {
      setUploadLoading(true);
      setUploadResults(null);
      const response = await axios.post('/api/credit-cards/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setUploadResults(response.data);
      setSuccess(`Upload completed: ${response.data.summary.created} created, ${response.data.summary.duplicates} duplicates, ${response.data.summary.errors} errors`);
      fetchCreditCards();
      
      // Don't close modal immediately if there are results to show
      if (response.data.summary.errors === 0) {
        setTimeout(() => setShowUploadModal(false), 2000);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.response?.data?.error || 'Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>Credit Cards</h3>
        {!isViewer && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200" onClick={() => setShowUploadModal(true)}>
              Upload CSV
            </button>
            <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2" onClick={handleNewCreditCard}>
              <Plus className="h-4 w-4" />
              Add Credit Card
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-error" style={{ position: 'relative', zIndex: 9999 }}>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" style={{ position: 'relative', zIndex: 9999 }}>
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <input
              type="text"
              placeholder="Search credit cards..."
              className="form-control"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="includeInactive"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <label htmlFor="includeInactive" style={{ margin: 0 }}>Include inactive</label>
          </div>
        </div>
      </div>

      {/* Credit Cards Table */}
      <div className="card">
        {loading ? (
          <div className="loading">
            <p>Loading credit cards...</p>
          </div>
        ) : creditCards.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>No credit cards found.</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Entity</th>
                <th>Last 4 CC</th>
                <th>Last 4 Bank Account</th>
                <th>Master Card</th>
                <th>Location</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {creditCards.map((creditCard) => (
                <tr key={creditCard.id}>
                  <td>
                    <strong>{creditCard.entity}</strong>
                    {creditCard.notes && (
                      <div style={{ fontSize: '0.9em', color: '#666', marginTop: '2px' }}>
                        {creditCard.notes}
                      </div>
                    )}
                  </td>
                  <td>{creditCard.lastFourCC}</td>
                  <td>{creditCard.lastFourBankAccount}</td>
                  <td>
                    {creditCard.masterCard ? (
                      <div>
                        <strong>{creditCard.masterCard.name}</strong>
                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                          {creditCard.masterCard.lastFourDigits} - {creditCard.masterCard.glAccount}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontStyle: 'italic' }}>No master card</span>
                    )}
                  </td>
                  <td>
                    {creditCard.location ? (
                      <div>
                        <strong>{creditCard.location.locationNumber}</strong>
                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                          {creditCard.location.locationName}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontStyle: 'italic' }}>No location</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${creditCard.isActive ? 'badge-active' : 'badge-inactive'}`}>
                      {creditCard.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(creditCard.createdAt).toLocaleDateString('en-US')}</td>
                  <td>
                    {!isViewer && (
                      <div className="relative">
                        <button
                          className="p-1 rounded-md hover:bg-gray-100 transition-colors duration-200"
                          onClick={(e) => {
                            if (openDropdownId === creditCard.id) {
                              setOpenDropdownId(null);
                              setOpenDropdownCreditCard(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const viewportHeight = window.innerHeight;
                              const dropdownHeight = 100; // Approximate height of dropdown

                              // Check if dropdown would go off the bottom of the screen
                              const shouldShowAbove = rect.bottom + dropdownHeight > viewportHeight;

                              setDropdownPosition({
                                top: shouldShowAbove ? rect.top - dropdownHeight : rect.bottom,
                                left: rect.left - 180
                              });
                              setOpenDropdownId(creditCard.id);
                              setOpenDropdownCreditCard(creditCard);
                            }
                          }}
                        >
                          <MoreVertical className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCreditCard ? 'Edit Credit Card' : 'Add New Credit Card'}</h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={handleCloseModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Entity *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.entity}
                  onChange={(e) => setFormData({ ...formData, entity: e.target.value })}
                  placeholder="Enter entity name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Last 4 CC *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.lastFourCC}
                  onChange={(e) => setFormData({ ...formData, lastFourCC: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  placeholder="Enter last 4 digits of credit card"
                  maxLength="4"
                  required
                />
              </div>

              <div className="form-group">
                <label>Last 4 Bank Account *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.lastFourBankAccount}
                  onChange={(e) => setFormData({ ...formData, lastFourBankAccount: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  placeholder="Enter last 4 digits of bank account"
                  maxLength="4"
                  required
                />
              </div>

              <div className="form-group">
                <label>Master Card</label>
                <SearchableSelect
                  value={formData.masterCardId}
                  onChange={(value) => setFormData({ ...formData, masterCardId: value })}
                  options={masterCards.filter(mc => mc.isActive)}
                  getOptionLabel={(mc) => `${mc.name} - ${mc.lastFourDigits} (${mc.glAccount})`}
                  getOptionValue={(mc) => mc.id}
                  filterOption={(mc, search) => {
                    const searchLower = search.toLowerCase();
                    return mc.name.toLowerCase().includes(searchLower) ||
                           mc.lastFourDigits.includes(searchLower) ||
                           mc.glAccount.toLowerCase().includes(searchLower);
                  }}
                  placeholder="Select a master card (optional)"
                  searchPlaceholder="Search master cards..."
                  emptyLabel="No Master Card"
                />
              </div>

              <div className="form-group">
                <label>Location</label>
                <SearchableSelect
                  value={formData.locationId}
                  onChange={(value) => setFormData({ ...formData, locationId: value })}
                  options={locations}
                  getOptionLabel={(loc) => `${loc.locationNumber} - ${loc.locationName}`}
                  getOptionValue={(loc) => loc.id}
                  filterOption={(loc, search) => {
                    const searchLower = search.toLowerCase();
                    return loc.locationNumber.toLowerCase().includes(searchLower) ||
                           loc.locationName.toLowerCase().includes(searchLower);
                  }}
                  placeholder="Select a location (optional)"
                  searchPlaceholder="Search locations..."
                  emptyLabel="No Location"
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  className="form-control"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Enter any notes"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <label htmlFor="isActive" style={{ margin: 0 }}>Active</label>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200">
                  {editingCreditCard ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload Credit Cards CSV</h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowUploadModal(false)}>×</button>
            </div>

            <div style={{ padding: '20px' }}>
              <p>Upload a CSV file with credit card data. The CSV should have these columns:</p>
              <ul>
                <li><strong>Entity</strong> - Company/Entity name</li>
                <li><strong>Last 4 CC</strong> - Last 4 digits of credit card</li>
                <li><strong>Last 4 Bank Account</strong> - Last 4 digits of bank account</li>
                <li><strong>Notes</strong> - Additional notes (optional)</li>
              </ul>
              
              {!uploadLoading && !uploadResults && (
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleUpload(file);
                    }
                  }}
                  className="form-control"
                />
              )}

              {uploadLoading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>Processing CSV file...</p>
                </div>
              )}

              {uploadResults && (
                <div style={{ marginTop: '20px' }}>
                  <h4>Upload Results</h4>
                  <div className="alert alert-success">
                    <ul style={{ margin: 0 }}>
                      <li>Total processed: {uploadResults.summary.total}</li>
                      <li>Created: {uploadResults.summary.created}</li>
                      <li>Linked to Master Cards: {uploadResults.summary.linkedToMasterCards}</li>
                      <li>Duplicates: {uploadResults.summary.duplicates}</li>
                      <li>Errors: {uploadResults.summary.errors}</li>
                    </ul>
                  </div>
                  
                  {uploadResults.errors && uploadResults.errors.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <h5>Errors:</h5>
                      <div className="alert alert-error">
                        <ul style={{ margin: 0 }}>
                          {uploadResults.errors.slice(0, 10).map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                          {uploadResults.errors.length > 10 && (
                            <li>... and {uploadResults.errors.length - 10} more errors</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: '15px', textAlign: 'right' }}>
                    <button
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200"
                      onClick={() => {
                        setUploadResults(null);
                        setShowUploadModal(false);
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fixed positioned dropdown menu */}
      {openDropdownId && openDropdownCreditCard && (
        <div
          className="fixed w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 dropdown-menu"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
              onClick={() => {
                handleEdit(openDropdownCreditCard);
                setOpenDropdownId(null);
                setOpenDropdownCreditCard(null);
              }}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Credit Card
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
              onClick={() => {
                setOpenDropdownId(null);
                setOpenDropdownCreditCard(null);
                handleDelete(openDropdownCreditCard);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Credit Card
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditCardManagement;